import { useEffect } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";

import { StaticValue } from "../seed/constants";
import { useResumes } from "../services/create-resume/queries";
import useSetResumeIdCookie from "./use-SetResumeIdCookie";

type ResumeLedgerItem = {
	_id: string;
	journal?: { lastUpdatedOn?: string };
};

/**
 * Only the screens that actually open a resume need the pointer recovered.
 *
 * This hook runs from RouteHandler, which wraps every route, so without this it
 * asked for the ledger on pages that have no business requesting one. On
 * /onboarding that is actively harmful: someone who has just registered holds a
 * valid token but has no backend user record yet, `GET resume-ledger` answers
 * 401, and the shared auth-failure policy bounces them to /signin — the screen
 * they just came from.
 */
function needsActiveResume(pathname: string): boolean {
	return pathname.startsWith("/resume/") || pathname === "/my-resumes" || pathname === "/view-pdf";
}

/**
 * Builder screens that are reachable before a resume exists. Everything else
 * under /resume/ edits a resume the server has to already hold.
 */
const BUILDER_ENTRY_ROUTES = new Set([
	"/resume/select-option",
	"/resume/select-experience",
	"/resume/select-template",
	"/resume/create-resume-options",
	"/resume/contact-information",
]);

function isBuilderEditRoute(pathname: string): boolean {
	return pathname.startsWith("/resume/") && !BUILDER_ENTRY_ROUTES.has(pathname);
}

/**
 * Keep the active-resume pointer honest. The resume-journal list on the server
 * is the source of truth; the `resume_id` cookie is only a hint that can go
 * stale. When the cookie is missing, or points at a resume the user no longer
 * has, adopt the user's most recently updated journal.
 *
 * This recovers the resume-in-progress after a session expires mid-flow: the
 * 401 sends the user to /signin, sign-in clears the `resume_id` cookie, and
 * without this the builder would re-open with no active resume and no way to
 * edit. Fetching the journals and re-selecting the current one restores it.
 */
const useRecoverActiveResume = () => {
	const staticValues = new StaticValue().getDefaultValues();
	// Watch the real cookie name — `useCookies` only re-renders for the names it
	// is given, and `["resume"]` names a cookie that does not exist.
	const [cookies] = useCookies([staticValues.localStorageResumeId]);
	const activeResumeId = cookies[staticValues.localStorageResumeId];
	const { pathname } = useLocation();

	// Auth-gated inside the hook too: a signed-out session never fetches.
	const {
		data: resumeList = [],
		isFetching,
		isSuccess,
	} = useResumes({
		enabled: needsActiveResume(pathname),
	}) as {
		data?: ResumeLedgerItem[];
		isFetching: boolean;
		isSuccess: boolean;
	};
	const { setResumeIdCookie, clearResumeIdCookie } = useSetResumeIdCookie();
	const navigate = useNavigate();

	useEffect(() => {
		// `isFetching` rather than `isLoading`: creating a resume invalidates the
		// list, and during that refetch the cached list is still the old one. Acting
		// on it would clear the pointer that was just written for the new resume.
		if (isFetching) return;

		// An anonymous author has this query disabled, so an empty list there means
		// "not asked", not "none exist" — they hold their resume through the cookie
		// alone and must be left alone.
		if (!isSuccess) return;

		const cookieIsCurrent = Boolean(activeResumeId) && resumeList.some((resume) => resume._id === activeResumeId);
		if (cookieIsCurrent) return;

		if (resumeList.length === 0) {
			// The pointer names a resume this reader does not have — deleted, or
			// carried over from another account in this browser. Left in place it
			// makes every save PATCH a dead id and the server answers 404, which
			// strands the reader on the page with no way forward. Dropping it sends
			// the contact-information screen down its create branch instead.
			if (activeResumeId) clearResumeIdCookie();

			// A signed-in reader with genuinely no resume cannot use an edit screen:
			// there is nothing to PATCH. Start them where a resume is created.
			if (isBuilderEditRoute(pathname)) {
				navigate("/resume/contact-information", { replace: true });
			}
			return;
		}

		// Most recently updated journal — the one the user was editing when a save
		// last ran. With a single resume (the common case) this is unambiguous.
		const mostRecent = [...resumeList].sort(
			(a, b) => new Date(b.journal?.lastUpdatedOn ?? 0).getTime() - new Date(a.journal?.lastUpdatedOn ?? 0).getTime(),
		)[0];

		if (mostRecent?._id) setResumeIdCookie(mostRecent._id);
	}, [activeResumeId, isFetching, isSuccess, pathname, navigate, resumeList, setResumeIdCookie, clearResumeIdCookie]);
};

export default useRecoverActiveResume;
